<template>
  <CDialog
    :visible.sync="visible"
    :title="$t('')"
    submittable
    :submit-label="$t('Save Changes')"
    :submit-loading="loading"
    width="840px"
    @submit="saveNotificationAndBudget"
    @open="resetData"
    :submitDisabled="!isAllowedBudgetSettingEdit"
  >
    <form @submit.prevent="saveNotificationAndBudget">
      <CTitle>{{ $t('Notification destination setting') }}</CTitle>

      <section>
        <CRow gutter="md">
          <CCol col="6">
            <CSelector
              :label="$t('Select Channel')"
              v-model="channels"
              :options="channelOptions"
              :disabled="loading"
              multiple
              useChips
            />
          </CCol>

          <CCol col="6">
            <CSelector
              :label="$t('Select Account')"
              v-model="accounts"
              :options="accountOptions"
              :disabled="loading"
            />
          </CCol>
        </CRow>
      </section>

      <CSeparator />

      <!--Budget - Daily-->
      <section>
        <CTitle margin-bottom>{{ $t('Setting per day') }}</CTitle>

        <CRow gutter="md">
          <CCol col="6">
            <div class="label">
              <label for="budgetDaily">
                {{ $t('Budget for a day') }}
                <span class="ml-2 text-1">({{ $t('Dollar') }})</span>
              </label>
              <el-switch v-model="enableDaily" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <div class="input-group">
              <div class="input-group-addon">$</div>
              <input
                type="number"
                class="form-control"
                placeholder="0"
                name="budgetDaily"
                id="budgetDaily"
                min="0"
                v-model="daily"
              />
            </div>
          </CCol>

          <CCol col="6">
            <div class="label">
              <label for="budgetDailyDiff">
                {{ $t('Day-over-day changes') }}
                <span class="ml-2 text-1">({{ $t('Percent') }})</span>
              </label>
              <el-switch v-model="enablePreviousDay" v-if="isAllowedBudgetSettingEdit" />
            </div>
            <div class="input-group">
              <div class="input-group-addon">%</div>
              <input
                type="number"
                class="form-control"
                placeholder="0"
                id="budgetDailyDiff"
                name="budgetDailyDiff"
                min="0"
                max="10000"
                v-model="previousDay"
              />
            </div>
          </CCol>
        </CRow>
      </section>

      <!--Budget - Monthly-->
      <section>
        <CTitle margin-bottom>{{ $t('Setting per month') }}</CTitle>

        <CRow gutter="md">
          <CCol col="6">
            <div class="label">
              <label for="budgetMonthly">
                {{ $t('Budget for a month') }}
                <span class="ml-2 text-1">({{ $t('Dollar') }})</span>
              </label>
              <el-switch v-model="enableMonthly" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <div class="input-group">
              <div class="input-group-addon">$</div>
              <input
                type="number"
                id="budgetMonthly"
                class="form-control"
                placeholder="0"
                name="budgetMonthly"
                min="0"
                v-model="monthly"
              />
            </div>
          </CCol>
        </CRow>
      </section>
    </form>
  </CDialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import isEqual from 'lodash.isequal';
import CDialog from '@/components/uikit/CDialog/index.vue';
import CTitle from '@/components/uikit/CTitle/index.vue';
import CRow from '@/components/uikit/CRow/index.vue';
import CCol from '@/components/uikit/CCol/index.vue';
import CSeparator from '@/components/uikit/CSeparator/index.vue';

import Icon from '@/components/uikit/Icon.vue';
import Button from '@/components/uikit/Button.vue';
import CustomInput from '@/components/uikit/CustomInput.vue';
import CSelector from '@/components/uikit/CSelector/index.vue';

const messages = {
  en: {
    'Alert setting': 'Alert Settings',
    'Save Changes': 'Save',
    'Notification destination setting': 'Notification Settings',
    'Email address': 'Email Address',
    Slack: 'Slack',
    'Email notification': 'Email Notification',
    'Slack notification': 'Slack Notification',
    'Please enter the correct e-mail address.': 'Please enter the correct e-mail address.',
    'Please enter the correct URL.': 'Please enter the correct URL.',
    "Please check Slack's webhooks documentation for further information.":
      "Please check Slack's webhooks documentation for more information.",
    'Setting per day': 'Daily Budget Alert Settings',
    'Setting per month': 'Monthly Budget Alert Settings',
    'Budget for a day': 'Total Amount',
    'Day-over-day changes': 'Charge',
    'Budget for a month': 'Total Amount',
    'Budget was set': 'Budget was set',
    Percent: '%',
    Dollar: '$',
    'Select Channel': 'Select Channel',
    'Select Account': 'Select Account',
    budget_alert_created: 'Budget Alert Created',
    budget_alert_edited: 'Budget Alert Edited',
    error_occurred: 'Error Occurred',
  },
  ja: {
    'Alert setting': 'アラートの設定',
    'Save Changes': '設定を保存',
    'Notification destination setting': '通知先の設定',
    'Email address': 'メールアドレス',
    Slack: 'Slack',
    'Email notification': 'メール通知',
    'Slack notification': 'Slack通知',
    'Please enter the correct e-mail address.': '正しいメールアドレスを入力してください。',
    'Please enter the correct URL.': '正しいURLを入力してください。',
    "Please check Slack's webhooks documentation for further information.":
      'webhookの設定に関しては公式のドキュメントを参照してください。',
    'Setting per day': '日毎の設定',
    'Setting per month': '月毎の設定',
    'Budget for a day': '１日の予算',
    'Day-over-day changes': '前日比',
    'Budget for a month': '１ヶ月の予算',
    'Budget was set': '予算の設定を変更しました',
    Percent: 'パーセント',
    Dollar: 'ドル',
    'Select Channel': 'チャンネルを選択',
    'Select Account': 'アカウントの選択',
    budget_alert_created: '予算アラート作成',
    budget_alert_edited: '予算アラート編集',
    error_occurred: 'エラー発生',
  },
};

export default {
  name: 'SettingBudgetModal',
  i18n: { messages },
  components: { CSelector, CDialog, CTitle, CRow, CCol, CSeparator, Icon, Button, CustomInput },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initObject: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      emailAddressIsWrong: false,
      slackUrlIsWrong: false,

      emailAddress: '',
      slackUrl: '',
      enableNotificationEmail: false,
      enableNotificationSlack: false,

      channels: [],
      accounts: '',

      daily: 0,
      previousDay: 0,
      monthly: 0,
      enableDaily: false,
      enablePreviousDay: false,
      enableMonthly: false,
    };
  },
  computed: {
    ...mapState('accounts', {
      selectedId: state => state.selectedId,
      details: state => state.details,
      fetchedAccounts: state => state.accounts,
    }),

    ...mapState({
      vendor: state => state.vendor,
    }),

    ...mapGetters('rbac', {
      isResourceAllowed: 'isResourceAllowed',
    }),

    ...mapGetters('notificationSettings', {
      notificationChannels: 'notificationChannels',
    }),

    isAllowedBudgetSettingEdit() {
      const initObjectWithoutId = { ...this.initObject };
      delete initObjectWithoutId.id;
      return (
        this.mode === 'create' ||
        (this.mode === 'edit' && !isEqual(this.payload, initObjectWithoutId))
      );
      // return (
      //   this.$display('usage_account_menu_budget_edit') &&
      //   this.$can('ModifyAccountSettings') &&
      //   this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.selectedId)
      // );
    },

    channelOptions() {
      return this.notificationChannels.map(channel => ({
        label: channel.name,
        value: channel.id,
      }));
    },

    accountOptions() {
      return this.fetchedAccounts.data.aws.map(({ account_id, account_name }) => ({
        label: account_name,
        value: account_id,
      }));
    },

    payload() {
      return {
        vendor: 'aws',
        accounts: [this.accounts.value],
        notification: {
          channels: this.channels.map(({ value }) => value),
          enabled: true,
        },
        daily: {
          value: this.daily,
          enabled: this.enableDaily,
        },
        dailyRate: {
          value: this.previousDay,
          enabled: this.enablePreviousDay,
        },
        monthly: {
          value: this.monthly,
          enabled: this.enableMonthly,
        },
      };
    },

    visible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    ...mapActions('accounts', ['fetchAccounts', 'fetchDetail']),
    ...mapActions('notificationSettings', [
      'postNotificationAndBudgetAlert',
      'putNotificationAndBudgetAlert',
    ]),

    async saveNotificationAndBudget() {
      this.loading = true;
      try {
        if (this.mode === 'create') {
          await this.postNotificationAndBudgetAlert(this.payload);
          this.$alpha.notify.custom({ type: 'success', message: this.$t('budget_alert_created') });
        } else {
          await this.putNotificationAndBudgetAlert({
            id: this.initObject.id,
            payload: this.payload,
          });
          this.$alpha.notify.custom({ type: 'success', message: this.$t('budget_alert_edited') });
        }
      } catch (err) {
        await this.$alpha.notify.custom({ type: 'danger', message: this.$t('error_occurred') });
      }
      this.loading = false;
      this.visible = false;
    },

    putBudget(params) {
      return this.$store.dispatch('accounts/putBudget', params);
    },

    resetData() {
      if (this.mode === 'create') {
        this.channels = [];
        this.accounts = '';
        this.daily = 0;
        this.previousDay = 0;
        this.monthly = 0;
        this.enableDaily = false;
        this.enablePreviousDay = false;
        this.enableMonthly = false;
      } else {
        this.daily = this.initObject.daily.value;
        this.previousDay = this.initObject.dailyRate.value;
        this.monthly = this.initObject.monthly.value;
        this.enableDaily = this.initObject.daily.enabled;
        this.enablePreviousDay = this.initObject.dailyRate.enabled;
        this.enableMonthly = this.initObject.monthly.enabled;
        const mappedAccounts = this.initObject.accounts.map(savedAccount =>
          this.accountOptions.find(account => account.value === savedAccount.id)
        );
        this.accounts = mappedAccounts.length ? mappedAccounts[0] : '';
        this.channels = this.initObject.notification.channels.map(savedNotification =>
          this.channelOptions.find(channel => channel.value === savedNotification.id)
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-footer {
  text-align: right;
  margin-top: 3rem;
}

.label {
  display: flex;
  justify-content: space-between;
}

label {
  display: block;
}

section {
  padding: $space-md 0;
}
</style>
